import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Role } from '../../../enum';
import { InputFormService, UserInfoService } from '../../../services';
import { MenuItemComponent } from './menu-item.component';
import { EnvironmentStore } from '../../environment/environment.store';

@Component({
  selector: 'rb-menu',
  imports: [NgIf, MenuItemComponent, NgForOf],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
})
export class MenuComponent implements OnInit {
  menuItems = signal<MenuItem[]>([]);
  readonly translate = inject(TranslateService);
  readonly inputFormService = inject(InputFormService);
  readonly userInfoService = inject(UserInfoService);
  readonly appInfo = inject(EnvironmentStore);

  hasAdminRole = computed(() => {
    const hasRole = this.userInfoService.hasRole([Role.ADMIN, Role.SECURITY_ADMIN]);
    const isAd = this.appInfo.environment()?.isAd === true;
    return !isAd && hasRole();
  });
  hasOnlySecurityRole = computed(() => {
    const userRoles = this.userInfoService.userRoles().filter(role => role !== Role.USER);
    return userRoles.length === 1 && userRoles[0] === Role.SECURITY_ADMIN;
  });

  constructor() {
    effect(() => {
      const menu = this.menuItems();
      const hasAdminRole = this.hasAdminRole();
      const hasOnlySecurityRole = this.hasOnlySecurityRole();
      if (menu.length > 0) {
        if (hasOnlySecurityRole) {
          menu.map(item => {
            if (!(item.id === 'home' || item.id === 'administration')) {
              item.visible = false;
            }
          });
        }
        const item = menu.find(item => item.id === 'administration');
        if (item) {
          item.visible = hasAdminRole;
        }
      }
    });
  }

  ngOnInit() {
    this.inputFormService.getUserForms().subscribe({
      next: forms => {
        this.menuItems.set([
          {
            id: 'home',
            label: this.translate.instant('menu.home'),
            items: [
              {
                id: 'dashboard',
                label: this.translate.instant('menu.dashboard'),
                icon: 'pi pi-fw pi-home',
                routerLink: ['/dashboard'],
              },
            ],
          },
          {
            id: 'laboratory',
            label: this.translate.instant('menu.laboratory'),
            items: forms.map(form => ({
              id: `lab-${form.id}`,
              label: form.nameCyr || form.name,
              icon: 'pi pi-fw pi-gauge',
              routerLink: ['/input-forms', form.id],
            })),
          },
          {
            id: 'codeBooks',
            label: this.translate.instant('menu.codeBooks'),
            items: [
              {
                id: 'ranneyWell',
                label: this.translate.instant('menu.ranneyWell'),
                icon: 'pi pi-fw pi-sliders-h',
                routerLink: ['/ranney-wells'],
              },
              {
                id: 'checkPoints',
                label: this.translate.instant('menu.checkPoints'),
                icon: 'pi pi-fw pi-cog',
                routerLink: ['/check-point'],
              },
              {
                id: 'measures',
                label: this.translate.instant('menu.measures'),
                icon: 'pi pi-fw pi-chart-line',
                routerLink: ['/measures'],
              },
              {
                id: 'unit-of-measures',
                label: this.translate.instant('menu.unit-of-measures'),
                icon: 'pi pi-fw pi-chart-bar',
                routerLink: ['/unit-of-measures'],
              },
              {
                id: 'validation-rules',
                label: this.translate.instant('menu.validationRules'),
                icon: 'pi pi-fw pi-shield',
                routerLink: ['/validation-rules'],
              },
            ],
          },
          {
            id: 'reports',
            label: this.translate.instant('menu.reports'),
            items: [
              {
                id: 'outOfRangeMeasures',
                label: this.translate.instant('menu.outOfRangeMeasures'),
                icon: 'pi pi-fw pi-arrows-v',
                routerLink: ['/reports/out-of-range-measures'],
              },
            ],
          },
          {
            id: 'administration',
            label: this.translate.instant('menu.administration'),
            visible: this.hasAdminRole(),
            items: [
              {
                id: 'user',
                label: this.translate.instant('menu.user'),
                icon: 'pi pi-fw pi-user',
                routerLink: ['/users'],
              },
            ],
          },
        ]);
      },
    });
  }
}
